import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  Input,
  OnChanges,
  ViewChild,
} from '@angular/core';
import { DeliveryMapComponent } from './delivery-map/delivery-map.component';
import { DeliveryTrack, isValidLatLng } from './delivery-tracking.models';
import {
  buildBanner,
  buildSteps,
  DeliveryBanner,
  DeliveryStep,
  formatPhone,
  telHref,
} from './delivery-tracking.view';

/**
 * Customer-facing delivery tracking: status banner, five-step tracker, live map,
 * proof-of-delivery photo and a "Call the store" action.
 *
 * Purely presentational: it renders whatever track it is given, so the order
 * status page (live data) and the demo page (simulated data) share it.
 */
@Component({
  selector: 'app-delivery-tracking',
  standalone: true,
  imports: [CommonModule, DeliveryMapComponent],
  templateUrl: './delivery-tracking.component.html',
  styleUrls: ['./delivery-tracking.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DeliveryTrackingComponent implements OnChanges {
  @Input() track: DeliveryTrack | null = null;
  /** IANA timezone of the store; times are shown in it. */
  @Input() timeZone: string | null = null;
  /** Used when the track response carries no store phone. */
  @Input() storePhone: string | null = null;
  /** False while the store has not accepted the order yet. */
  @Input() orderAccepted = true;

  @ViewChild('closeButton') closeButton: ElementRef<HTMLButtonElement>;
  @ViewChild('photoButton') photoButton: ElementRef<HTMLButtonElement>;

  banner: DeliveryBanner | null = null;
  steps: DeliveryStep[] = [];
  showMap = false;
  callHref = '';
  callLabel = '';
  podPhotoUrl: string | null = null;
  podNote: string | null = null;
  photoOpen = false;
  photoFailed = false;
  private mapFailed = false;

  constructor(private cdr: ChangeDetectorRef) {}

  ngOnChanges(): void {
    const track = this.track;
    if (!track) {
      this.banner = null;
      this.steps = [];
      this.showMap = false;
      this.podPhotoUrl = null;
      return;
    }
    const options = { timeZone: this.timeZone, orderAccepted: this.orderAccepted };
    this.banner = buildBanner(track, options);
    this.steps = buildSteps(track, options);

    this.showMap =
      !this.mapFailed &&
      track.live_map === true &&
      (track.state === 'EN_ROUTE' || track.state === 'ARRIVED') &&
      isValidLatLng(track.position) &&
      isValidLatLng(track.destination);

    const phone = (track.store && track.store.phone) || this.storePhone;
    this.callHref = telHref(phone);
    this.callLabel = this.callHref ? formatPhone(phone) : '';

    const photo = track.state === 'DELIVERED' && track.pod ? track.pod.photo_url || null : null;
    if (photo !== this.podPhotoUrl) {
      this.photoFailed = false;
    }
    this.podPhotoUrl = photo;
    this.podNote = track.state === 'DELIVERED' && track.pod ? track.pod.note || null : null;
    if (!photo) {
      this.photoOpen = false;
    }
  }

  get emphasiseCall(): boolean {
    return !!this.track && (this.track.state === 'FAILED' || this.track.state === 'CANCELLED');
  }

  trackStep(_: number, step: DeliveryStep): string {
    return step.key;
  }

  stepStatusText(step: DeliveryStep): string {
    switch (step.status) {
      case 'done':
        return 'completed';
      case 'current':
        return 'in progress';
      case 'failed':
        return 'not completed';
      default:
        return 'not started';
    }
  }

  onMapUnavailable(): void {
    this.mapFailed = true;
    this.showMap = false;
    this.cdr.markForCheck();
  }

  onPhotoError(): void {
    this.photoFailed = true;
    this.photoOpen = false;
  }

  openPhoto(): void {
    this.photoOpen = true;
    setTimeout(() => {
      if (this.closeButton) {
        this.closeButton.nativeElement.focus();
      }
    });
  }

  closePhoto(): void {
    if (!this.photoOpen) {
      return;
    }
    this.photoOpen = false;
    setTimeout(() => {
      if (this.photoButton) {
        this.photoButton.nativeElement.focus();
      }
    });
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.photoOpen) {
      this.closePhoto();
      this.cdr.markForCheck();
    }
  }
}
